import {
    SegmentedCarousel,
    useSegmentedCarouselController,
} from '@eventbrite/collection-carousel';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { useIsMobileDevice } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { Typography } from '@eventbrite/marmalade';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ALL_TAB, FOR_YOU } from '../../../../constants/tabConfig';
import { useHasEnteredViewport } from '../../../../hooks';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import { getPopularCityEvents } from '../../api/popularCityEvents';
import styles from './PopularEventsCarousel.module.scss';
import RankedCardContainer from './RankedCardContainer';
import { shouldEnableCarousel } from './shouldEnableCarousel';

const CarouselTrack = ({
    carouselLoading,
    locale,
    popularEvents,
}: {
    carouselLoading: boolean;
    locale: string;
    popularEvents: FormattedEvent[];
}) => {
    return (
        <SegmentedCarousel.Track>
            {carouselLoading
                ? Array.from({ length: 8 }, (_, i) => (
                      <LoadingSkeleton height={320} key={i} />
                  ))
                : popularEvents.map((event, i) => (
                      <RankedCardContainer
                          event={event}
                          key={i}
                          locale={locale}
                          rank={i + 1}
                      />
                  ))}
        </SegmentedCarousel.Track>
    );
};

export const DisconnectedPopularEventsCarousel = ({
    currentPlace,
    isAuthenticated,
    isFeLoading,
    locale,
    placeId,
    tabKey,
}: PopularEventsCarouselProps) => {
    const [enableCarousel, setEnableCarousel] = useState(false);
    const [carouselLoading, setCarouselLoading] = useState(true);
    const [popularEvents, setPopularEvents] = useState<FormattedEvent[]>([]);

    const enrollmentRef = useRef(null);
    const { hasEnteredViewport } = useHasEnteredViewport(enrollmentRef);

    const controllerProps = useSegmentedCarouselController();
    const { setShouldUpdateCarousel } = controllerProps;

    const headerText = gettext('Top Trending in %(currentPlace)s', {
        currentPlace,
    });

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        const enrollAndfetchEvents = async (isAuthenticated: boolean) => {
            if (shouldEnableCarousel(isAuthenticated) && placeId) {
                setEnableCarousel(true);
                try {
                    const popularEvents = await getPopularCityEvents(
                        placeId,
                        signal,
                    );

                    if (!signal.aborted) {
                        setPopularEvents(popularEvents);
                    }
                } catch (e) {
                    setPopularEvents([]);
                    console.error(e);
                }
                setCarouselLoading(false);
                setShouldUpdateCarousel(true);
            }
        };

        if (hasEnteredViewport && placeId) {
            if (
                (tabKey === ALL_TAB && !isAuthenticated) ||
                (tabKey === FOR_YOU && isAuthenticated)
            ) {
                enrollAndfetchEvents(isAuthenticated);
            } else {
                setPopularEvents([]);
                setCarouselLoading(false);
                setShouldUpdateCarousel(true);
            }
        }

        return () => controller.abort();
    }, [
        hasEnteredViewport,
        placeId,
        isAuthenticated,
        setShouldUpdateCarousel,
        tabKey,
    ]);

    const showCarousel =
        enableCarousel &&
        !isFeLoading &&
        (carouselLoading ? true : !!popularEvents.length);

    const isMobile = useIsMobileDevice(1219);

    if (tabKey !== ALL_TAB && tabKey !== FOR_YOU) {
        return null;
    }

    return (
        <div className={styles.root} ref={enrollmentRef}>
            {showCarousel && (
                <div className={styles.carouselContainer}>
                    <SegmentedCarousel
                        {...controllerProps}
                        hideOverflow={!isMobile}
                        rootPaddingLeft={0}
                        rootPaddingRight={isMobile ? undefined : 20}
                    >
                        <SegmentedCarousel.Header>
                            <Typography
                                className={styles.carouselHeader}
                                variant="heading-sm"
                                color="black"
                            >
                                {headerText}
                            </Typography>
                        </SegmentedCarousel.Header>
                        <CarouselTrack
                            carouselLoading={carouselLoading}
                            locale={locale}
                            popularEvents={popularEvents}
                        />
                        <SegmentedCarousel.Footer />
                    </SegmentedCarousel>
                </div>
            )}
        </div>
    );
};

const _mapStateToProps = ({ app, content, user, location }: DiscoverState) => {
    return {
        currentPlace: location?.currentPlace,
        isAuthenticated: !!user.isAuthenticated,
        isFeLoading: !!app.loadingState?.isFeLoading,
        locale: app.locale,
        placeId: location.placeId,
        tabKey: content.browseState?.tabKey,
    };
};

type ReduxProps = ConnectedProps<typeof _mapStateToProps>;
type PopularEventsCarouselProps = ReduxProps;

export default connect(_mapStateToProps)(DisconnectedPopularEventsCarousel);
